import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import type { SxProps } from "@mui/material/styles";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import TextField from "@mui/material/TextField";
import type { muiTheme } from "@polifonia/theme";
import { theme } from "@polifonia/theme";
import { useMemo } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
} from "@/components/Accordion";
import { Typography } from "@/components/Typography";
import { Trans } from "@/features/i18n/Trans";
import type { InterludeTypes } from "@/interlude";
import type { WebStreamConfigPage } from "@/pages/channels/pages/detail/wccs/WebStreamConfig";

type WebStreamConfig = InterludeTypes["WebStreamConfig"];

const styles = {
  section: {
    backgroundColor: theme.colors.white,
    borderRadius: "8px",
  },
  fieldColumn: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  languageTabs: {
    marginBottom: "16px",
  },
} satisfies Record<string, SxProps<typeof muiTheme>>;

namespace MessagesForm {
  export interface Props {
    selectedLocale: WebStreamConfigPage.LocaleKey;
    setSelectedLocale: (locale: WebStreamConfigPage.LocaleKey) => void;
  }
}

// `label` holds a `<Trans>` element rather than a key string fed to `t()` in the
// render loop: a key that only ever reaches `t()` through a variable is invisible to
// every tool that scans for `t("literal")` — i18n-ally shows no inline preview and
// flags nothing missing, and an unused-key sweep reports these as dead and removes
// them. Keeping the keys as literals here is what makes them findable. Module-level
// JSX is safe because `@/features/i18n/Trans` calls `useTranslation()` internally,
// so each mounted instance re-renders on a language change. `name` stays a literal
// so the `Controller` form paths below keep type-checking.
const MENU_MESSAGE_FIELDS = [
  {
    name: "title",
    label: <Trans i18nKey="webStreamConfig.messages.menu.title" />,
  },
  {
    name: "subtitle",
    label: <Trans i18nKey="webStreamConfig.messages.menu.subtitle" />,
  },
  {
    name: "teamTabText",
    label: <Trans i18nKey="webStreamConfig.messages.menu.teamTabText" />,
  },
  {
    name: "memberTabText",
    label: <Trans i18nKey="webStreamConfig.messages.menu.memberTabText" />,
  },
  {
    name: "searchPlaceholder",
    label: <Trans i18nKey="webStreamConfig.messages.menu.searchPlaceholder" />,
  },
  {
    name: "searchClear",
    label: <Trans i18nKey="webStreamConfig.messages.menu.searchClear" />,
  },
  {
    name: "categoryAll",
    label: <Trans i18nKey="webStreamConfig.messages.menu.categoryAll" />,
  },
  {
    name: "consult",
    label: <Trans i18nKey="webStreamConfig.messages.menu.consult" />,
  },
  {
    name: "historyEmpty",
    label: <Trans i18nKey="webStreamConfig.messages.menu.historyEmpty" />,
  },
  {
    name: "findTeam",
    label: <Trans i18nKey="webStreamConfig.messages.menu.findTeam" />,
  },
] as const;

const REENTRY_MESSAGE_FIELDS = [
  {
    name: "title",
    label: <Trans i18nKey="webStreamConfig.messages.reentry.title" />,
  },
  {
    name: "description",
    label: <Trans i18nKey="webStreamConfig.messages.reentry.description" />,
  },
  {
    name: "buttonText",
    label: <Trans i18nKey="webStreamConfig.messages.reentry.buttonText" />,
  },
] as const;

export const MessagesForm: React.FC<MessagesForm.Props> =
  function MessagesForm({ selectedLocale, setSelectedLocale }) {
    const { t } = useTranslation();
    const { control } = useFormContext<WebStreamConfig>();

    const localeLabels: Record<WebStreamConfigPage.LocaleKey, string> = useMemo(
      () => ({
        enUs: t("webStreamConfig.messages.locale.en"),
        zhTw: t("webStreamConfig.messages.locale.zhTw"),
        ja: t("webStreamConfig.messages.locale.ja"),
        th: t("webStreamConfig.messages.locale.th"),
      }),
      [t],
    );

    return (
      <Accordion>
        <AccordionSummary>
          <Typography variant="h3" fontWeight={500}>
            {t("webStreamConfig.messages.title")}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Stack sx={styles.section} gap="16px">
            <Tabs
              value={selectedLocale}
              onChange={(_, newValue) => setSelectedLocale(newValue)}
              sx={styles.languageTabs}
            >
              {(
                Object.keys(
                  localeLabels,
                ) as Array<WebStreamConfigPage.LocaleKey>
              ).map((locale) => (
                <Tab key={locale} value={locale} label={localeLabels[locale]} />
              ))}
            </Tabs>
            <Stack gap="16px" key={selectedLocale}>
              <Typography variant="h3" fontWeight={500}>
                {t("webStreamConfig.messages.menu.sectionTitle")}
              </Typography>
              {MENU_MESSAGE_FIELDS.map(({ name, label }) => (
                <Box key={name} sx={styles.fieldColumn}>
                  <Typography variant="body">{label}</Typography>
                  {/*
                   * The banner title is the only field here whose line breaks change the
                   * layout, so it is the only one that carries a hint: the bar holds two
                   * lines, and a third grows it past 128px, which crops the hero image
                   * behind it because that image is sized with `cover`.
                   */}
                  {name === "title" ? (
                    <Typography variant="note">
                      <Trans i18nKey="webStreamConfig.messages.menu.title.hint" />
                    </Typography>
                  ) : null}
                  <Controller
                    name={`menu.messages.${selectedLocale}.${name}`}
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        fullWidth
                        // Multiline so a tenant can place the break themselves rather than
                        // padding the title with spaces and letting the viewport wrap it,
                        // which lands differently on every screen width.
                        multiline={name === "title"}
                        // minRows, not the fixed `rows` the reentry description below uses:
                        // the field opens at the two lines the hint recommends, and a third
                        // line still grows the field instead of being hidden.
                        {...(name === "title" ? { minRows: 2 } : {})}
                        error={!!error}
                        helperText={error?.message}
                      />
                    )}
                  />
                </Box>
              ))}
              <Typography variant="h3" fontWeight={500}>
                {t("webStreamConfig.messages.reentry.sectionTitle")}
              </Typography>
              {REENTRY_MESSAGE_FIELDS.map(({ name, label }) => (
                <Box key={name} sx={styles.fieldColumn}>
                  <Typography variant="body">{label}</Typography>
                  <Controller
                    name={`reentry.messages.${selectedLocale}.${name}`}
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        fullWidth
                        multiline={name === "description"}
                        {...(name === "description" ? { rows: 3 } : {})}
                        error={!!error}
                        helperText={error?.message}
                      />
                    )}
                  />
                </Box>
              ))}
            </Stack>
          </Stack>
        </AccordionDetails>
      </Accordion>
    );
  };
